import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { switchMap, map, catchError, take, finalize } from 'rxjs/operators';
import * as MachineListActions from './machine-list.actions';
import { TMachineListInfoActionType } from './machine-list.models';
import { FactoryFacade } from '../factory/factory.facade';
import { MachineListFacade } from './machine-list.facade';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import { LIST_ITEMS_PER_PAGE_LIST } from '../../constants/constants';

@Injectable({ providedIn: 'root' })
export class MachineListEffects {
  constructor(
    private actions$: Actions,
    private mmDataService: MachineMaintenanceDataService,
    private factoryFacade: FactoryFacade,
    private machineListFacade: MachineListFacade,
    private APIHelper: AppNotificationUtilClass,
  ) {}

  getMachineList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.GetMachineList),
      switchMap((action: MachineListActions.GetMachineListInfo) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        this.machineListFacade.getPagination().pipe(
          take(1),
          map((pagination) => {
            return [...args, pagination] as const;
          }),
        ),
      ),
      switchMap((args) =>
        this.machineListFacade.getListingTab().pipe(
          take(1),
          map((listingTab) => {
            return [...args, listingTab] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId, pagination, listingTab]) =>
        this.mmDataService
          .fetchMachineListData({
            factoryId,
            page: pagination.pageNo,
            itemsPerPage: pagination.pageSize,
            selectedFilters: action.selectedFilters,
            searchStrOpt: action.searchStrOpt,
            listingTabOpt: listingTab,
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(loaderEl);
            }),
            map((res) => new MachineListActions.GetMachineListInfoSuccess(res, action.fetchReason)),
            catchError(() => {
              return of(new MachineListActions.GetMachineListInfoFailure());
            }),
          ),
      ),
    ),
  );

  getMachineDropDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.GetMachineListFilterDropDown),
      switchMap(() =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return factoryId;
          }),
        ),
      ),
      switchMap((factoryId) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, factoryId] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, factoryId]) =>
        from(this.mmDataService.getMachineListFilterDropDownValues(factoryId)).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((data) => new MachineListActions.GetMachineListFilterDropDownInfoSuccess(data)),
          catchError(() => {
            return of(new MachineListActions.GetMachineListFilterDropDownInfoFailure());
          }),
        ),
      ),
    ),
  );

  getMachineTransferDropDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.GetMachineTransferDropdown),
      switchMap((action: MachineListActions.MachineTransferDropdown) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        from(this.mmDataService.getMachineTransferDropdown({ factorySerialNumber: action.factorySerialNumber })).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((data) => new MachineListActions.MachineTransferDropdownSuccess(data)),
          catchError(() => {
            return of(new MachineListActions.MachineTransferDropdownFailure());
          }),
        ),
      ),
    ),
  );

  machineTransfer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.MachineTransfer),
      switchMap((action: MachineListActions.MachineTransfer) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        this.mmDataService
          .transferToExternalLocation({
            factoryId,
            factorySerialNumber: action.payload.factorySerialNumber,
            locationId: action.payload.locationId,
            transferReason: action.payload.transferReason,
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(loaderEl);
            }),
            map(() => {
              this.machineListFacade.resetMachineListInfo('ACTION');
              return new MachineListActions.MachineTransferSuccess();
            }),
            catchError(() => {
              return of(new MachineListActions.MachineTransferFailure());
            }),
          ),
      ),
    ),
  );

  deleteMachines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.DeleteMachines),
      switchMap((action: MachineListActions.DeleteMachines) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        from(
          this.mmDataService.deleteMachines({
            factoryId,
            factorySerialNumbers: action.factorySerialNumbers,
            itemsPerPage: LIST_ITEMS_PER_PAGE_LIST[0], // TODO: [anubhav] remove hardcoded value in later releases
          }),
        ).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.machineListFacade.resetMachineListInfo('ACTION');
            return new MachineListActions.DeleteMachinesSuccess();
          }),
          catchError(() => {
            return of(new MachineListActions.DeleteMachinesFailure());
          }),
        ),
      ),
    ),
  );

  bulkUpdateStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.BulkUpdateStatus),
      switchMap((action: MachineListActions.BulkUpdateStatus) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.bulkUpdateMachineStatus(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.machineListFacade.resetMachineListInfo('ACTION');
            return new MachineListActions.BulkUpdateStatusSuccess();
          }),
          catchError(() => {
            return of(new MachineListActions.BulkUpdateStatusFailure());
          }),
        ),
      ),
    ),
  );

  bulkUpdatePrintedStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.BulkUpdatePrintedStatus),
      switchMap((action: MachineListActions.BulkUpdatePrintedStatus) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.bulkUpdateMachineQRPrintedStatus(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.machineListFacade.resetMachineListInfo('ACTION');
            return new MachineListActions.BulkUpdatePrintedStatusSuccess();
          }),
          catchError(() => {
            return of(new MachineListActions.BulkUpdatePrintedStatusFailure());
          }),
        ),
      ),
    ),
  );

  getBulkAssignDropDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.GetBulkAssignLocationDropdown),
      switchMap(() =>
        this.factoryFacade.getFactoryId().pipe(
          map((factoryId) => {
            return factoryId;
          }),
        ),
      ),
      switchMap((factoryId) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, factoryId] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, factoryId]) =>
        from(this.mmDataService.getBulkLocationDropdown(factoryId)).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((data) => new MachineListActions.GetBulkAssignLocationDropdownSuccess(data)),
          catchError(() => {
            return of(new MachineListActions.GetBulkAssignLocationDropdownFailure());
          }),
        ),
      ),
    ),
  );

  bulkAssignLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.AssignBulkLocation),
      switchMap((action: MachineListActions.BulkAssignLocation) =>
        this.factoryFacade.getFactoryId().pipe(
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        from(
          this.mmDataService.assignBulkLocation({
            factoryId,
            factorySerialNumbers: action.payload.factorySerialNumbers,
            //TODO[Anubhav]: throwing console error
            locationId: action.payload.locationId,
          }),
        ).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.machineListFacade.resetMachineListInfo('ACTION');
            return new MachineListActions.BulkAssignLocationSuccess();
          }),
          catchError(() => {
            return of(new MachineListActions.BulkAssignLocationFailure());
          }),
        ),
      ),
    ),
  );

  getSiblingFactoriesDropDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.GetSiblingFactoriesDropdown),
      switchMap(() =>
        this.factoryFacade.getFactoryId().pipe(
          map((factoryId) => {
            return factoryId;
          }),
        ),
      ),
      switchMap((factoryId) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, factoryId] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, factoryId]) =>
        from(this.mmDataService.getSiblingFactories()).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((data) => new MachineListActions.GetSiblingFactoriesDropdownSuccess(data)),
          catchError(() => {
            return of(new MachineListActions.GetSiblingFactoriesDropdownFailure());
          }),
        ),
      ),
    ),
  );

  transferToSiblingFactory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.TransferToSiblingFactory),
      switchMap((action: MachineListActions.TransferToSiblingFactory) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        from(this.mmDataService.transferToSiblingFactory(action.payload)).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.machineListFacade.resetMachineListInfo('ACTION');
            return new MachineListActions.TransferToSiblingFactorySuccess();
          }),
          catchError(() => {
            return of(new MachineListActions.TransferToSiblingFactoryFailure());
          }),
        ),
      ),
    ),
  );

  bulkUpdatePreventiveSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.BulkUpdatePreventiveSchedule),
      switchMap((action: MachineListActions.BulkUpdatePreventiveSchedule) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        from(this.mmDataService.bulkUpdatePreventiveSchedule(action.payload)).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.machineListFacade.resetMachineListInfo('ACTION');
            return new MachineListActions.BulkUpdatePreventiveScheduleSuccess();
          }),
          catchError(() => {
            return of(new MachineListActions.BulkUpdatePreventiveScheduleFailure());
          }),
        ),
      ),
    ),
  );

  getMachineAndLineDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TMachineListInfoActionType.GetMachineAndLineDetails),
      switchMap((action: MachineListActions.GetMachineAndLineDetails) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        from(
          this.mmDataService.getMachineAndLineDetails({ factoryId, factorySerialNumber: action.factorySerialNumber }),
        ).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((data) => new MachineListActions.GetMachineAndLineDetailsSuccess(data)),
          catchError(() => {
            return of(new MachineListActions.GetMachineAndLineDetailsFailure());
          }),
        ),
      ),
    ),
  );
}
